/* ==========================================================================
   FNDRY - Skin Duo explainer sheet (P9 2026-10-21)

   The bundle card under the buy box ("Both halves as the Skin Duo") used to
   send a tap straight to the Duo page. A tap on the card, or on its link, now
   opens a short sheet first and she chooses:

   - "See the Skin Duo" goes to the bundle product, carrying this page's query
     string so ad parameters (utm_*, fbclid) survive. `variant` and
     `selling_plan` are dropped: they name this product's variants, not the
     Duo's.
   - "Stay with Collagen" closes the sheet and scrolls back to the buy box.
   - The X, Escape and a tap on the backdrop close it.

   A native <dialog> opened with showModal(): the rest of the page is inert
   while it is open, Tab is kept inside it, the page does not scroll behind it,
   and focus goes back to the card's link when it closes. A bottom sheet on
   phones, a centred modal from 750px - CSS only, in fndry-pdp-buy.css.

   Failure direction: with this script absent or thrown, or a browser without
   <dialog>, nothing here runs, the dialog stays `hidden`, and the card's link
   goes to the Duo page exactly as it did before. A tap is only intercepted
   once the sheet is known to open.

   Clarity: one custom event per action (duo_sheet_open, duo_sheet_see_duo,
   duo_sheet_stay), each guarded - Clarity is an app embed and may be absent.

   Markup: sections/fndry-pdp-buy.liquid, the "bundle" block.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__fndryDuoSheet) return;
  window.__fndryDuoSheet = true;

  var CLOSE_MS = 280; /* matches the closing animation in the stylesheet */
  var DROP = ['variant', 'selling_plan'];

  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var current = null; /* { sheet, card } while a sheet is open */
  var closeTimer = null;
  var warmObserver = null;

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reduced() {
    return !!(reduceMq && reduceMq.matches);
  }

  function track(name) {
    try {
      if (window.clarity) window.clarity('event', name);
    } catch (e) {}
  }

  function usable(sheet) {
    return !!sheet && typeof sheet.showModal === 'function';
  }

  function sheetFor(card) {
    var id = card && card.getAttribute('data-duo-card');
    var sheet = id ? document.getElementById(id) : null;
    return sheet && sheet.hasAttribute('data-duo-sheet') ? sheet : null;
  }

  /* The bundle URL with this page's query string on it, minus the parameters
     that only mean something on this product. */
  function carry(url) {
    var search = window.location.search || '';
    if (search.length < 2) return url;
    var extra = search.slice(1);
    try {
      var params = new URLSearchParams(search);
      DROP.forEach(function (key) { params.delete(key); });
      extra = params.toString();
    } catch (e) {}
    if (!extra) return url;
    var hash = '';
    var at = url.indexOf('#');
    if (at > -1) {
      hash = url.slice(at);
      url = url.slice(0, at);
    }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + extra + hash;
  }

  /* --fndry-header-h may be authored in px or rem, and the root is 10px. */
  function cssLength(value, fallback) {
    var raw = String(value || '').trim();
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    if (raw.indexOf('rem') > -1) {
      return n * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 10);
    }
    return n;
  }

  function lock(on) {
    document.documentElement.classList.toggle('fndry-duo-lock', on);
    document.body.classList.toggle('fndry-no-scroll', on);
  }

  /* The sheet's image is lazy and sits in a closed dialog, so the browser
     would only start fetching it on open. Start it while the card is
     approaching instead, so the sheet opens on a loaded picture. */
  function warm(sheet) {
    if (!sheet) return;
    slice(sheet.querySelectorAll('img[loading="lazy"]')).forEach(function (img) {
      img.loading = 'eager';
    });
  }

  function focusables(sheet) {
    return slice(sheet.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
  }

  /* ---------- open / close ------------------------------------------------ */

  function open(sheet, card, silent) {
    if (!usable(sheet) || sheet.open) return false;
    if (current) finish(current, null, true);

    var see = sheet.querySelector('[data-duo-see]');
    if (see) {
      if (!see.hasAttribute('data-href')) see.setAttribute('data-href', see.getAttribute('href') || '');
      see.setAttribute('href', carry(see.getAttribute('data-href')));
    }
    warm(sheet);

    sheet.removeAttribute('data-closing');
    sheet.removeAttribute('hidden');
    try {
      sheet.showModal();
    } catch (e) {
      sheet.setAttribute('hidden', '');
      return false;
    }
    current = { sheet: sheet, card: card };
    lock(true);
    if (!silent) track('duo_sheet_open');
    return true;
  }

  /* Back to the card she came from - its link, or the card itself - without
     moving the page: "stay" scrolls on its own, after this. */
  function refocus(card) {
    if (!card || !document.contains(card)) return;
    var target = card.querySelector('[data-duo-open]') || card;
    if (target === card && !card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');
    try {
      target.focus({ preventScroll: true });
    } catch (e) {}
  }

  function toBuyBox(card) {
    var root = card && card.closest ? card.closest('[data-fndry-pdp]') : null;
    if (!root) return;
    var target = root.querySelector('#buy') || root.querySelector('.fndry-pdp__form');
    if (!target) return;
    var header = cssLength(getComputedStyle(document.documentElement).getPropertyValue('--fndry-header-h'), 68);
    var top = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) - header - 16;
    try {
      window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: reduced() ? 'auto' : 'smooth' });
    } catch (e) {
      window.scrollTo(0, Math.max(0, Math.round(top)));
    }
  }

  /* Tear down, once. `quiet` skips focus and scroll (editor, back/forward). */
  function finish(state, how, quiet) {
    if (!state || current !== state) return;
    current = null;
    window.clearTimeout(closeTimer);
    var sheet = state.sheet;
    try {
      if (sheet.open) sheet.close();
    } catch (e) {}
    sheet.removeAttribute('data-closing');
    sheet.setAttribute('hidden', '');
    lock(false);
    if (quiet) return;
    refocus(state.card);
    if (how === 'stay') toBuyBox(state.card);
  }

  function close(how) {
    var state = current;
    if (!state) return;
    var sheet = state.sheet;
    if (sheet.hasAttribute('data-closing')) return;
    if (reduced() || !sheet.open) {
      finish(state, how, false);
      return;
    }
    sheet.setAttribute('data-closing', '');
    closeTimer = window.setTimeout(function () {
      finish(state, how, false);
    }, CLOSE_MS);
  }

  /* ---------- clicks ------------------------------------------------------- */

  function onSheetClick(event, sheet) {
    if (!current || current.sheet !== sheet) return;
    var t = event.target;

    /* A tap on the backdrop lands on the dialog element itself. */
    if (t === sheet) {
      var r = sheet.getBoundingClientRect();
      var inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) close();
      return;
    }
    if (t.closest('[data-duo-close]')) {
      event.preventDefault();
      close();
      return;
    }
    if (t.closest('[data-duo-stay]')) {
      event.preventDefault();
      track('duo_sheet_stay');
      close('stay');
      return;
    }
    if (t.closest('[data-duo-see]')) {
      track('duo_sheet_see_duo'); /* the link itself does the navigating */
    }
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    var sheet = t.closest('[data-duo-sheet]');
    if (sheet) {
      onSheetClick(event, sheet);
      return;
    }

    var card = t.closest('[data-duo-card]');
    if (!card) return;
    var link = t.closest('a[href]');
    var trigger = card.querySelector('[data-duo-open]');
    if (link && link !== trigger) return;
    /* A new-tab or new-window click on the link keeps doing that. */
    if (link && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) return;

    var target = sheetFor(card);
    if (!usable(target)) return;
    if (open(target, card, false)) event.preventDefault();
  });

  /* Escape. Prevented so the sheet can animate out and hand focus back; if a
     browser closes it regardless, the close listener below tidies up. */
  document.addEventListener(
    'cancel',
    function (event) {
      if (!current || event.target !== current.sheet) return;
      event.preventDefault();
      close();
    },
    true
  );

  document.addEventListener(
    'close',
    function (event) {
      if (current && event.target === current.sheet) finish(current, null, false);
    },
    true
  );

  /* Tab stays inside the sheet (the browser would otherwise also cycle
     through its own toolbar). */
  document.addEventListener('keydown', function (event) {
    if (!current || event.key !== 'Tab') return;
    var items = focusables(current.sheet);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (!current.sheet.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Back from the Duo page (restored from the back/forward cache): nothing may
     be left open or locked on a page she can see again. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && current) finish(current, null, true);
  });

  /* ---------- set-up -------------------------------------------------------- */

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var cards = slice(context.querySelectorAll('[data-duo-card]'));
    if (context.matches && context.matches('[data-duo-card]')) cards.unshift(context);

    cards.forEach(function (card) {
      if (card.getAttribute('data-duo-ready') === 'true') return;
      var sheet = sheetFor(card);
      if (!usable(sheet)) return;
      card.setAttribute('data-duo-ready', 'true');
      var trigger = card.querySelector('[data-duo-open]');
      if (trigger) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', sheet.id);
      }
      if (warmObserver) warmObserver.observe(card);
    });
  }

  if (window.IntersectionObserver) {
    warmObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          warm(sheetFor(entry.target));
          warmObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '800px 0px' }
    );
  }

  function boot() {
    try {
      init();
    } catch (e) {
      if (window.console && console.error) console.error('[fndry-duo-sheet]', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------- theme editor ------------------------------------------------- */

  document.addEventListener('shopify:section:load', function (event) {
    try {
      init(event.target);
    } catch (e) {}
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (current && event.target && event.target.contains && event.target.contains(current.sheet)) {
      finish(current, null, true);
    }
  });

  /* Selecting the bundle block opens its sheet, so its settings can be seen. */
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target && event.target.closest ? event.target.closest('[data-duo-card]') : null;
    if (!card) return;
    var sheet = sheetFor(card);
    if (usable(sheet) && !sheet.open) open(sheet, card, true);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var card = event.target && event.target.closest ? event.target.closest('[data-duo-card]') : null;
    if (card && current && current.card === card) finish(current, null, true);
  });
})();
